import React, { useCallback, useEffect, useRef, useState } from "react";
import globalAxios from "axios";
import { FiUploadCloud, FiLock, FiTool } from "react-icons/fi";
import { useRealtime, useRealtimeEvent } from "../Realtime/RealtimeProvider";

/** Live state of the deploy currently running (or the lock held by someone else). */
type DeployState = {
	target: string;
	stage?: string;
	current?: number;
	total?: number;
	title?: string | null;
	user?: string;
	message?: string;
	locked?: boolean;
	/** Seeded from the deploy-lock snapshot rather than a live event: coarse, and safe to replace/clear. */
	fromLock?: boolean;
	/** « maintenance » quand le verrou est tenu par un réalignement/hard-clean, « deploy » sinon. */
	kind?: "maintenance" | "deploy";
	/** Progression réelle du job de maintenance (snapshot du coordinateur), si disponible. */
	job?: { done?: number; total?: number } | null;
};

/**
 * Compact live deployment indicator for the top bar (rendered before the presence widget). Driven by
 * the realtime « deploy » event : which rides the SignalR Redis backplane, so a deploy started on ANY
 * API instance shows here for EVERY connected admin. Shows the target (preview/production), the
 * current NAMED job (« Alignement des composants dynamiques », « Indexation recherche »…), a running
 * N/total with a progress bar, and who triggered it. Hidden when nothing is deploying; a « lock » event
 * surfaces a deploy already in progress held by another admin.
 *
 * Events alone are not enough to be CORRECT, only to stay correct:
 *  • on mount (F5, a new tab, arriving mid-deploy) there is no event to replay : SignalR broadcasts are
 *    fire-and-forget with no buffer : so the bar would stay blank until the next progress tick, or
 *    forever if the deploy is inside one long stage;
 *  • on reconnect the provider replays only the heartbeat, so anything broadcast while the socket was
 *    down is lost for good.
 * Both are closed by seeding from the deploy-lock snapshot, which is server state and therefore true
 * regardless of what this client did or did not hear. Same shape as PreviewPill: snapshot + subscribe.
 */
export const DeployProgressBar: React.FC = () => {
	const [state, setState] = useState<DeployState | null>(null);
	const hideTimer = useRef<number | undefined>(undefined);
	const mountedRef = useRef(true);
	const { connected } = useRealtime();

	const clearHide = () => {
		if (hideTimer.current) window.clearTimeout(hideTimer.current);
		hideTimer.current = undefined;
	};

	/**
	 * Read the server's view of what is deploying right now. Production only: it is the only target
	 * that takes a lock (the preview auto-worker takes it too, under its own name).
	 *
	 * Deliberately does NOT overwrite live event state : an in-flight deploy we are already tracking
	 * carries a real stage + N/total, which this snapshot cannot (the lock holds only owner/target).
	 * The snapshot's job is to fill the gap when we know nothing; the next tick refines it.
	 */
	const seedFromLock = useCallback(async () => {
		try {
			const res = await globalAxios.get(`/api/deployment/lock`, {
				params: { target: "production" },
				withCredentials: true,
				headers: { Accept: "application/json" }
			});
			if (!mountedRef.current) return;
			const lockedBy = res.data?.lockedBy as string | undefined;
			setState((prev) => {
				// Live event state describes a deploy we are actually tracking (real stage + N/total):
				// never clobber it with the coarser lock. Only fill in, or clear what we ourselves seeded.
				if (prev && !prev.fromLock) return prev;
				if (!lockedBy) return null; // lock released ⇒ drop the seeded notice
				const kind = (res.data?.kind as "maintenance" | "deploy" | undefined) ??
					(lockedBy.startsWith("maintenance:") ? "maintenance" : "deploy");
				return {
					target: res.data?.target ?? "production",
					user: (res.data?.displayName as string | undefined) ?? lockedBy,
					locked: true,
					fromLock: true,
					kind,
					job: res.data?.maintenance ?? null,
					message: kind === "maintenance" ? "Maintenance en cours" : "Déploiement en cours"
				};
			});
		} catch {
			// No lock info is not an error state: leave the bar as-is rather than flashing something wrong.
		}
	}, []);

	const onDeploy = useCallback((p: any) => {
		if (!p) return;
		const phase = p.phase as string | undefined;

		if (phase === "finish") {
			// Let the bar linger a moment at 100% so a fast deploy doesn't just flicker, then hide.
			clearHide();
			hideTimer.current = window.setTimeout(() => setState(null), 1600);
			return;
		}

		clearHide();
		if (phase === "lock") {
			setState({ target: p.target ?? "production", user: p.user, locked: true, message: "Déploiement déjà en cours" });
			// A lock notice is transient : clear it if nothing else follows.
			hideTimer.current = window.setTimeout(() => setState(null), 4000);
			return;
		}

		if (phase === "start" || phase === "progress") {
			setState((prev) => ({
				target: p.target ?? prev?.target ?? "preview",
				stage: p.stage ?? prev?.stage,
				current: typeof p.current === "number" ? p.current : prev?.current,
				total: typeof p.total === "number" ? p.total : prev?.total,
				title: p.title ?? prev?.title ?? null,
				user: p.user ?? prev?.user,
				message: p.message ?? prev?.message,
				locked: false
			}));
		}
	}, []);

	useRealtimeEvent("deploy", onDeploy);

	useEffect(() => {
		mountedRef.current = true;
		return () => {
			mountedRef.current = false;
			clearHide();
		};
	}, []);

	// Seed on mount, and re-seed whenever the socket (re)connects: a reconnect means we may have missed
	// a start/finish entirely, so the server's lock : not our event history : decides what is true.
	useEffect(() => {
		if (connected) void seedFromLock();
	}, [connected, seedFromLock]);
	useEffect(() => {
		void seedFromLock();
	}, [seedFromLock]);

	// A snapshot-seeded "locked" state has no finish event to clear it (we never saw the start), so
	// re-read the lock until it releases. Only while showing that state : no idle polling.
	useEffect(() => {
		if (!state?.locked) return;
		const id = window.setInterval(() => void seedFromLock(), 15000);
		return () => window.clearInterval(id);
	}, [state?.locked, seedFromLock]);

	if (!state) return null;

	const targetLabel = state.target === "production" ? "Production" : "Preview";
	const tone =
		state.target === "production"
			? "border-amber-200 bg-amber-50 text-amber-800"
			: "border-sky-200 bg-sky-50 text-sky-800";
	const pct =
		state.total && state.total > 0
			? Math.min(100, Math.round(((state.current ?? 0) / state.total) * 100))
			: null;

	if (state.locked) {
		// Un job de maintenance (réalignement, hard-clean) n'est pas un déploiement : bandeau
		// dédié, progression réelle, et un intitulé qui explique pourquoi la publication attend.
		if (state.kind === "maintenance") {
			const progress =
				typeof state.job?.done === "number" && typeof state.job?.total === "number" && state.job.total > 0
					? ` · ${state.job.done}/${state.job.total}`
					: "";
			return (
				<div
					className="flex items-center gap-2 rounded-full border border-slate-200 bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700"
					title={`Maintenance (réalignement) en cours${state.user ? ` par ${state.user}` : ""} : les publications reprendront automatiquement à la fin du job.`}
				>
					<FiTool className="h-3.5 w-3.5 shrink-0 animate-pulse" />
					<span className="truncate">Maintenance en cours{state.user ? ` · ${state.user}` : ""}{progress}</span>
				</div>
			);
		}
		return (
			<div className={`flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-semibold ${tone}`} title={`Déploiement en cours${state.user ? ` par ${state.user}` : ""}`}>
				<FiLock className="h-3.5 w-3.5 shrink-0" />
				<span className="truncate">Déploiement en cours{state.user ? ` · ${state.user}` : ""}</span>
			</div>
		);
	}

	const job = state.stage || state.message || "Déploiement…";

	return (
		<div className={`min-w-[210px] max-w-[340px] rounded-xl border px-3 py-1.5 ${tone}`} role="status" aria-live="polite">
			<div className="flex items-center gap-2">
				<FiUploadCloud className="h-3.5 w-3.5 shrink-0 animate-pulse" />
				<span className="shrink-0 text-[10px] font-bold uppercase tracking-wide">{targetLabel}</span>
				<span className="min-w-0 flex-1 truncate text-xs font-semibold" title={state.title ? `${job} : ${state.title}` : job}>
					{job}
				</span>
				{state.total ? (
					<span className="shrink-0 text-[11px] font-semibold tabular-nums">
						{Math.min(state.current ?? 0, state.total)}/{state.total}
					</span>
				) : null}
			</div>
			{/* Progress bar: determinate when we know the batch size, indeterminate pulse otherwise. */}
			<div className="mt-1 h-1 w-full overflow-hidden rounded-full bg-black/10">
				<div
					className={`h-full rounded-full ${state.target === "production" ? "bg-amber-500" : "bg-sky-500"} ${pct === null ? "w-1/3 animate-pulse" : "transition-[width] duration-300"}`}
					style={pct === null ? undefined : { width: `${pct}%` }}
					role="progressbar"
					aria-valuenow={pct ?? undefined}
					aria-valuemin={0}
					aria-valuemax={100}
				/>
			</div>
		</div>
	);
};

export default DeployProgressBar;
